import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import Popup from '../../../../../../Utilities/components/Popup';

import {
  getPaymentMethodById,
  updatePaymentMethod,
  deletePaymentMethod,
  setDefaultPaymentMethod
} from '../../../../../../Utilities/api';

import { StateContext } from '../../../../../../StateProvider';

import {
  WhiteBackButton,
  DeleteButton
} from '../../../../../../Utilities/components/Buttons';

import Footer from '../../../../../../Footer';
import './EditPaymentMethod.css';

export default function EditPaymentMethod() {
  const params = useParams();
  const id = params ? params.id : null;
  const navigate = useNavigate();

  const ctx = useContext(StateContext);
  const user = ctx ? ctx.user : null;
  const refreshUserData = ctx ? ctx.refreshUserData : null;
  const loading = ctx ? ctx.loading : false;
  const setLoading = ctx ? ctx.setLoading : function () {};

  const [paymentMethod, setPaymentMethod] = useState(null);
  const [cardholderName, setCardholderName] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [error, setError] = useState(null);

  // ✅ LOCAL loading ONLY for default action
  const [settingDefault, setSettingDefault] = useState(false);

  // ---------- HELPERS ----------
  function resolveErrorMessage(err) {
    if (err && err.response && err.response.data && err.response.data.message) {
      return err.response.data.message;
    }
    if (err && err.message) {
      return err.message;
    }
    return 'Something went wrong. Please try again.';
  }

  function isValidStripeId(value) {
    if (!value) return false;
    if (value.length < 3) return false;
    return value.substring(0, 3) === 'pm_';
  }

  function formatTwoDigits(value) {
    var s = String(value);
    if (s.length === 1) return '0' + s;
    return s;
  }

  function lastTwoDigits(value) {
    var s = String(value);
    if (s.length <= 2) return s;
    return s.substring(s.length - 2);
  }

  var validId = isValidStripeId(id);

  // ---------- LOAD PAYMENT METHOD ----------
  useEffect(function () {
    async function load() {
      if (!id || !validId) return;

      try {
        var method = await getPaymentMethodById(id);

        if (!method) {
          navigate('/profile/wallet/payment-methods');
          return;
        }

        setPaymentMethod(method);

        if (method.cardholderName) {
          setCardholderName(method.cardholderName);
        }
      } catch (err) {
        console.error('Failed to load payment method:', err);
        navigate('/profile/wallet/payment-methods');
      }
    }

    load();
  }, [id, validId, navigate]);

  // ---------- SAVE ----------
  async function handleSave(e) {
    e.preventDefault();

    if (!paymentMethod || !user) return;

    if (!user.stripeCustomerId) {
      setError('Your payment profile is not fully set up. Please add a card first.');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      await updatePaymentMethod(user.uid, {
        paymentMethodId: paymentMethod.stripePaymentMethodId,
        stripeCustomerId: user.stripeCustomerId,
        cardholderName: cardholderName
      });

      await refreshUserData(user.uid);
      navigate(-1);

    } catch (err) {
      console.error('Update failed:', err);
      setError(resolveErrorMessage(err));
    }

    setLoading(false);
  }

  // ---------- SET DEFAULT ----------
  async function handleSetDefault() {
    if (!paymentMethod || !user) return;

    var pmId = String(paymentMethod.stripePaymentMethodId);
    var currentDefault = '';

    if (user.defaultPaymentMethodId) {
      currentDefault = String(user.defaultPaymentMethodId);
    }

    if (currentDefault === pmId) return;

    setSettingDefault(true);

    try {
      await setDefaultPaymentMethod(user.uid, pmId);
      await refreshUserData(user.uid);
      navigate(-1);
    } catch (err) {
      console.error('Failed to set default:', err);
      setError(resolveErrorMessage(err));
    }

    setSettingDefault(false);
  }

  // ---------- DELETE ----------
  async function handleDelete() {
    if (!paymentMethod || !user) return;

    var deletingId = String(paymentMethod.stripePaymentMethodId);
    var wasDefault =
      user.defaultPaymentMethodId &&
      String(user.defaultPaymentMethodId) === deletingId;

    setLoading(true);

    try {
      await deletePaymentMethod(deletingId);
      await refreshUserData(user.uid);

      if (wasDefault && user.paymentMethods && user.paymentMethods.length > 1) {
        var i = 0;
        var next = null;

        while (i < user.paymentMethods.length) {
          if (
            user.paymentMethods[i] &&
            String(user.paymentMethods[i].stripePaymentMethodId) !== deletingId
          ) {
            next = user.paymentMethods[i];
            break;
          }
          i++;
        }

        if (next && next.stripePaymentMethodId) {
          await setDefaultPaymentMethod(user.uid, next.stripePaymentMethodId);
          await refreshUserData(user.uid);
        }
      }

      navigate('/profile/wallet');

    } catch (err) {
      console.error('Delete failed:', err);
      setError(resolveErrorMessage(err));
    }

    setLoading(false);
    setShowPopup(false);
  }

  var loaded = paymentMethod !== null;
  var isDefault = false;

  if (
    user &&
    paymentMethod &&
    user.defaultPaymentMethodId &&
    paymentMethod.stripePaymentMethodId
  ) {
    isDefault =
      String(user.defaultPaymentMethodId) ===
      String(paymentMethod.stripePaymentMethodId);
  }

  // ---------- RENDER ----------
  return (
    <>
      <div className="edit-payment-backdrop">
        <div className="container">

          <Popup isOpen={showPopup} handleClose={function () { setShowPopup(false); }}>
            <p className="payment-edit-popup-header">Delete Payment Method?</p>
            <div className="payment-edit-popup-button-container">
              <button
                className="payment-edit-popup-cancel"
                onClick={function () { setShowPopup(false); }}
              >
                Cancel
              </button>
              <button
                className="payment-edit-popup-delete"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </Popup>

          <div className="payment-edit-button-container">
            <WhiteBackButton />
            <DeleteButton onDelete={function () { setShowPopup(true); }} />
          </div>

          <div className="edit-payment-method">
            <h2 className="edit-payment-header">Update Payment Method</h2>

            {!loaded ? (
              <p>Loading...</p>
            ) : (
              <>
                <div className="edit-payment-info">
                  <p className="edit-payment-info-header">Card Info</p>
                  <hr className="edit-payment-breakline" />

                  <p>Card Ending: {paymentMethod.cardNumber}</p>
                  <p>Type: {paymentMethod.cardType}</p>
                  <p>
                    Expires{' '}
                    {formatTwoDigits(paymentMethod.expiryMonth)}/
                    {lastTwoDigits(paymentMethod.expiryYear)}
                  </p>
                </div>

                <form>
                  <label>Cardholder Name</label>
                  <input
                    type="text"
                    value={cardholderName}
                    onChange={function (e) {
                      setCardholderName(e.target.value);
                      if (error) setError(null);
                    }}
                  />
                </form>

                {error && (
                  <p className="stripe-error-message">⚠️ {error}</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="edit-payment-method-submit-container">
        {loaded ? (
          <div className="default-payment-method">
            <button
              disabled={isDefault || settingDefault}
              onClick={handleSetDefault}
            >
              {isDefault
                ? 'Default Card'
                : settingDefault
                  ? 'Setting...'
                  : 'Set as Default'}
            </button>
          </div>
        ) : null}

        <button
          onClick={handleSave}
          className="edit-payment-method-submit"
          disabled={loading || !loaded}
        >
          {loading ? 'Processing...' : 'Save'}
        </button>
      </div>

      <Footer />
    </>
  );
}
